import { useState, useEffect, useCallback } from "react";
import { Box, Button, TextField, Typography, CircularProgress, IconButton } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";

import DataGridCustom from "../General/DatagridCustom";
import AppDialog from "../General/Dialog/AppDialog";
import ConfirmDialog from "../General/Dialog/ConfirmDialog";
import CurrencyInput from "../General/CurrencyInput";
import { useToast } from "../../context/ToastContext";
import { formatCurrency } from "../../config/Helper";

function fmtDate(v) {
  if (!v) return "—";
  return new Date(v).toLocaleDateString("es-AR");
}

/**
 * ABM de valores con fecha de vigencia + monto (Caja de Abogados, Colegio, JUS…).
 * Compartido entre la configuración de municipalidad y la individual: solo cambian
 * las funciones de API.
 */
export default function DateAmountPanel({
  icon,
  title,
  subtitle,
  fetchFn,
  createFn,
  updateFn,
  deleteFn,
  amountLabel = "Monto ($)",
}) {
  const { showToast } = useToast();

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [filterModel, setFilterModel] = useState({ items: [], quickFilterValues: [] });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formDate, setFormDate] = useState(null);
  const [formAmount, setFormAmount] = useState(null);
  const [formNotes, setFormNotes] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    const search = filterModel.quickFilterValues?.join(" ") || "";
    try {
      const res = await fetchFn({ perPage: paginationModel.pageSize, page: paginationModel.page + 1, search });
      setRows(res.data?.data ?? []);
      setTotal(res.data?.total ?? 0);
    } catch {
      showToast("Error al cargar registros.", "error");
    } finally {
      setLoading(false);
    }
  }, [fetchFn, paginationModel, filterModel, showToast]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    setFormDate(null);
    setFormAmount(null);
    setFormNotes("");
    setDialogOpen(true);
  };
  const openEdit = (row) => {
    setEditing(row);
    setFormDate(row.effective_from ? dayjs(row.effective_from) : null);
    setFormAmount(row.amount ?? null);
    setFormNotes(row.notes ?? "");
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formDate || formAmount == null) {
      showToast("Completá la fecha y el monto.", "error");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        effective_from: formDate.format("YYYY-MM-DD"),
        amount: formAmount,
        notes: formNotes || null,
      };
      if (editing) await updateFn(editing.id, payload);
      else await createFn(payload);
      setDialogOpen(false);
      load();
      showToast(editing ? "Registro actualizado." : "Registro creado.", "success");
    } catch (e) {
      showToast(e?.response?.data?.message ?? "Error al guardar.", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteFn(deleteTarget.id);
      setDeleteTarget(null);
      load();
      showToast("Registro eliminado.", "success");
    } catch {
      showToast("Error al eliminar.", "error");
    }
  };

  const columns = [
    { field: "effective_from", headerName: "Vigencia", flex: 1, renderCell: (p) => fmtDate(p.value) },
    {
      field: "amount",
      headerName: amountLabel,
      flex: 1,
      align: "right",
      headerAlign: "right",
      renderCell: ({ value }) => formatCurrency(value),
    },
    {
      field: "notes",
      headerName: "Notas",
      flex: 2,
      renderCell: (p) =>
        p.value ? (
          <Typography variant="caption" sx={{ opacity: 0.7, fontStyle: "italic" }}>
            {p.value}
          </Typography>
        ) : null,
    },
    {
      field: "actions",
      headerName: "",
      flex: 0.5,
      sortable: false,
      renderCell: (p) => (
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <IconButton size="small" onClick={() => openEdit(p.row)}>
            <EditRoundedIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" color="error" onClick={() => setDeleteTarget(p.row)}>
            <DeleteRoundedIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <>
      <DataGridCustom
        rows={rows}
        columns={columns}
        icon={icon}
        title={title}
        description={subtitle}
        loading={loading}
        total={total}
        paginationModel={paginationModel}
        onPaginationChange={setPaginationModel}
        filterModel={filterModel}
        onFilterChange={(m) => {
          setFilterModel(m);
          setPaginationModel((p) => ({ ...p, page: 0 }));
        }}
        filterMode="server"
        isServerPagination
        pageSizeOptions={[5, 10, 25, 50]}
        headerActions={
          <Button variant="contained" size="small" startIcon={<AddRoundedIcon />} onClick={openCreate} sx={{ borderRadius: 2.5, fontWeight: 900 }}>
            Agregar
          </Button>
        }
      />

      <AppDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={editing ? "Editar registro" : "Nuevo registro"}
        maxWidth="xs"
        fullWidth
        stickyHeader
        actions={
          <>
            <Button onClick={() => setDialogOpen(false)} disabled={saving}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveRoundedIcon />}
              onClick={handleSave}
              disabled={saving}
              sx={{ borderRadius: 2, fontWeight: 900 }}
            >
              Guardar
            </Button>
          </>
        }
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 0.5 }}>
          <DatePicker
            label="Fecha de vigencia"
            value={formDate}
            onChange={setFormDate}
            format="DD/MM/YYYY"
            slotProps={{ textField: { size: "small", fullWidth: true } }}
          />
          <CurrencyInput
            label={amountLabel}
            value={formAmount}
            onValueChange={({ floatValue }) => setFormAmount(floatValue ?? null)}
            size="small"
            fullWidth
          />
          <TextField
            label="Notas (opcional)"
            value={formNotes}
            onChange={(e) => setFormNotes(e.target.value)}
            size="small"
            multiline
            rows={2}
          />
        </Box>
      </AppDialog>

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Eliminar registro"
        description={`¿Eliminar el registro de ${fmtDate(deleteTarget?.effective_from)}?`}
        confirmText="Eliminar"
        confirmColor="error"
      />
    </>
  );
}
